"use client"

import { Note } from "@/types/admin"
import { CheckCircle2, XCircle, Users, MessageSquare, Clock } from "lucide-react"
import UserAvatar from "@/components/UserAvatar"
import { capitalizeFirstLetter, cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"

interface RecentNotesGridProps {
  notes: Note[]
  onNoteClick?: (noteId: string) => void
}

export default function RecentNotesGrid({ notes, onNoteClick }: RecentNotesGridProps) {
  if (notes.length === 0) {
    return (
      <div className="text-center py-8 bg-muted/20 rounded-xl border border-dashed border-muted-foreground/25">
        <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
          <MessageSquare className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-sm font-medium text-foreground mb-1">Brak ostatnich notatek</h3>
        <p className="text-xs text-muted-foreground">Tutaj pojawią się najnowsze wpisy.</p>
      </div>
    )
  }

  return (
    // ZMIANA: Używamy Grid zamiast Columns, aby wiersze były równe
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {notes.map((note) => {
        const createdDate = new Date(note.createdAt)
        const createdDateLabel = createdDate.toLocaleDateString('pl-PL', {
          day: '2-digit',
          month: '2-digit'
        })
        const createdTimeLabel = createdDate.toLocaleTimeString('pl-PL', {
          hour: '2-digit',
          minute: '2-digit'
        })

        return (
          <div
            key={note.id}
            // ZMIANA: h-full zapewnia, że element wypełnia komórkę grida
            className="group h-full focus:outline-none"
            onClick={() => onNoteClick ? onNoteClick(note.id) : window.location.href = `/notes`}
          >
            {/* ZMIANA: flex-col i h-full wewnątrz karty rozciąga ją, by dorównać sąsiadowi */}
            <div className="flex flex-col h-full bg-white dark:bg-zinc-900/50 rounded-lg border border-border/60 hover:border-blue-400/50 dark:hover:border-blue-500/50 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden">

              {/* Header: Klient */}
              <div className="bg-muted/30 p-3 border-b border-border/50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {note.client.imageUrl ? (
                      <img
                        src={note.client.imageUrl}
                        alt={note.client.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Users className="w-4 h-4 text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground truncate leading-tight">
                      {note.client.name}
                    </h4>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                      <Clock className="w-3 h-3" />
                      <span>{createdDateLabel}, {createdTimeLabel}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content: Treść */}
              {/* ZMIANA: flex-1 sprawia, że ta sekcja zajmuje dostępne miejsce, wypychając footer na dół */}
              <div className="p-3 flex-1">
                <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                  {note.content}
                </p>
              </div>

              {/* Footer: Status i Autor */}
              {/* ZMIANA: mt-auto gwarantuje, że footer jest zawsze na samym dole karty */}
              <div className="mt-auto px-3 py-2 bg-muted/10 border-t border-border/40 flex items-center justify-between gap-2">

                {/* Autor */}
                <div className="flex items-center gap-1.5 overflow-hidden">
                  <UserAvatar
                    name={note.createdBy.username}
                    imageUrl={note.createdBy.avatarUrl}
                    size="xs"
                    className="h-5 w-5"
                  />
                  <span className="text-[10px] font-medium text-muted-foreground truncate max-w-[80px]">
                    {capitalizeFirstLetter(note.createdBy.username)}
                  </span>
                </div>

                {/* Status */}
                <div className="flex-shrink-0">
                  {note.isRead ? (
                    <div className="flex items-center gap-1">
                      {/* Jeśli jest readBy, pokazujemy avatar osoby czytającej */}
                      {note.readBy && (
                        <div className="hidden xs:block mr-1" title={`Przeczytał: ${capitalizeFirstLetter(note.readBy.username)}`}>
                          <UserAvatar
                            name={note.readBy.username}
                            imageUrl={note.readBy.avatarUrl}
                            size="xs"
                            className="h-4 w-4 opacity-70"
                          />
                        </div>
                      )}
                      <Badge variant="secondary" className="bg-emerald-100/50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 text-[10px] px-1.5 h-5 font-normal border-emerald-200/50 dark:border-emerald-800/50">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Odczytana
                      </Badge>
                    </div>
                  ) : (
                    <Badge variant="secondary" className="bg-red-100/50 text-red-700 dark:bg-red-900/20 dark:text-red-400 text-[10px] px-1.5 h-5 font-normal border-red-200/50 dark:border-red-800/50">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5 animate-pulse" />
                      Nowa
                    </Badge>
                  )}
                </div>

              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}